import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDownRight, Play, Pause } from "lucide-react";
import { HERO_IMAGES, CONTACT } from "../data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="gd-reveal-mask">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

const slideVariants = {
  enter: (dir) => ({ x: dir >= 0 ? "100%" : "-100%" }),
  center: { x: "0%" },
  exit: (dir) => ({ x: dir >= 0 ? "-100%" : "100%" }),
};

export const Hero = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [playing, setPlaying] = useState(true);
  const len = HERO_IMAGES.length;
  const touch = useRef({ x: 0, y: 0 });

  // Preload all hero images once for smooth, instant transitions
  useEffect(() => {
    HERO_IMAGES.forEach((src) => {
      const im = new Image();
      im.decoding = "async";
      im.src = src;
    });
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !playing) return;
    const t = setTimeout(() => {
      setDir(1);
      setIdx((v) => (v + 1) % len);
    }, 5000);
    return () => clearTimeout(t);
  }, [idx, len, playing]);

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
  };

  const go = (delta) => {
    setDir(delta);
    setIdx((v) => (v + delta + len) % len);
  };
  const goTo = (i) => {
    setDir(i >= idx ? 1 : -1);
    setIdx(i);
  };

  return (
    <section
      id="top"
      className="relative min-h-screen w-full overflow-hidden pt-28"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Slideshow background */}
      <div className="absolute inset-0 z-0 overflow-hidden" data-testid="hero-slideshow" aria-hidden="true">
        <AnimatePresence initial={false} custom={dir} mode="sync">
          <motion.img
            key={idx}
            src={HERO_IMAGES[idx]}
            alt=""
            custom={dir}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.95, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
            decoding="async"
            fetchPriority="high"
            style={{ willChange: "transform" }}
          />
        </AnimatePresence>
        {/* Desktop wash: elegant left-to-right gradient */}
        <div className="absolute inset-0 hidden sm:block bg-gradient-to-r from-[#FCFBF8]/92 via-[#FCFBF8]/45 to-[#FCFBF8]/10" />
        <div className="absolute inset-0 hidden sm:block bg-gradient-to-t from-[#FCFBF8] via-transparent to-transparent" />
        {/* Mobile wash: stronger dim + bottom fade so bold text is clearly readable over the photo */}
        <div className="absolute inset-0 sm:hidden bg-[rgba(252,251,248,0.82)]" />
        <div className="absolute inset-0 sm:hidden bg-gradient-to-t from-[#FCFBF8] via-[#FCFBF8]/62 to-[#FCFBF8]/48" />
      </div>

      {/* soft radial festive glow */}
      <div
        className="pointer-events-none absolute -right-40 top-10 z-0 h-[560px] w-[560px] rounded-full opacity-30 blur-[120px]"
        style={{ background: "radial-gradient(circle, #EAD39C 0%, rgba(240,148,31,0.25) 45%, transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto max-w-[1500px] px-6 pt-10 sm:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-14 bg-[#C5A059]" />
          <span className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-[#A37E3A]">
            Luxury Weddings & Events · Mangaluru
          </span>
        </motion.div>

        <h1 className="font-heading text-[15vw] font-semibold leading-[0.86] tracking-tighter text-[#141312] sm:font-light sm:text-[13vw] sm:text-[#1A1918] lg:text-[10.5rem]">
          <Line i={0}>Crafting</Line>
          <Line i={1}>
            <span className="italic text-gradient-gold">everlasting</span>
          </Line>
          <Line i={2}>
            <span className="flex items-end gap-4">
              memories
              <ArrowDownRight
                className="mb-3 hidden h-10 w-10 text-[#C5A059] lg:block"
                strokeWidth={1.2}
              />
            </span>
          </Line>
        </h1>

        <div className="mt-12 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="max-w-md rounded-2xl bg-[rgba(252,251,248,0.78)] px-4 py-3 font-body text-base font-medium leading-relaxed text-[#201e1a] backdrop-blur-[2px] sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:font-normal sm:text-[#3d3b37] sm:backdrop-blur-none lg:col-span-5 lg:text-lg"
          >
            From the first spark of an idea to the last dance, Golden Decor designs
            immersive celebrations — decor, lighting, entertainment and chauffeured
            luxury — woven into one seamless, unforgettable experience.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.9 }}
            className="flex flex-wrap gap-4 lg:col-span-7 lg:justify-end"
          >
            <a
              href="#services"
              data-testid="hero-explore-btn"
              className="rounded-full bg-[#1A1918] px-8 py-4 font-body text-sm font-medium text-[#FCFBF8] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#0D0C0A]"
            >
              Explore Our Craft
            </a>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-plan-btn"
              className="rounded-full border border-[#1A1918]/25 bg-[#FCFBF8]/60 px-8 py-4 font-body text-sm font-medium text-[#1A1918] backdrop-blur-sm transition-colors duration-300 hover:border-[#C5A059] hover:text-[#A37E3A]"
            >
              Plan Your Event
            </a>
          </motion.div>
        </div>

        {/* Slide controls */}
        <div className="mt-12 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="hero-slide-dots">
            {HERO_IMAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                data-testid={`hero-slide-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === idx ? "w-9 bg-[#C5A059]" : "w-2 bg-[#1A1918]/25 hover:bg-[#C5A059]/60"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPlaying((p) => !p)}
              data-testid="hero-slide-playpause"
              aria-label={playing ? "Pause slideshow" : "Play slideshow"}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1A1918] text-[#FCFBF8] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#0D0C0A]"
            >
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Editorial marquee */}
      <div className="relative z-10 mt-14 border-y border-[#E6E2D6] bg-[#F3F1EC]/70 py-5 backdrop-blur-sm lg:mt-20">
        <Marquee speed={45} gradient={false} autoFill>
          {["Mangaluru", "Valencia", "Luxury Weddings", "Event Decor", "Sound & Light", "Entertainment", "Chauffeured Cars"].map(
            (t, i) => (
              <span key={i} className="mx-8 inline-flex items-center gap-8 font-body text-sm uppercase tracking-[0.25em] text-[#5C5A56]">
                {t}
                <span className="text-[#C5A059]">✦</span>
              </span>
            )
          )}
        </Marquee>
      </div>
    </section>
  );
};
